import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { api, call } from '@/api/client';
import { useMe } from '@/api/hooks/core';
import { qk } from '@/api/keys';
import { plansQuery } from '@/features/account/billing';
import { PITCH, keyPoints } from '@/features/account/planPoints';
import { Button, Icon, Modal } from '@/components/ui';
import { upgradePrompt } from '@/lib/upgradePrompt';
import s from './UpgradePrompt.module.css';

const PLAN_NAME = { pro: 'Pro', elite: 'Elite' } as const;

/**
 * The dialog every plan lock opens (`lib/upgradePrompt`): what was locked, what the plan that lifts
 * it includes, and the way to it — the plans page, where the web checkout is. Mounted once by the
 * shell.
 *
 * **The trial is offered only when this account would get it.** The same two facts the plans page
 * and the checkout read: `trial_eligible`, and no referral discount (the half-price month replaces
 * the trial, STR-04). An account that has had its trial is offered the plan, never a trial it would
 * be refused at checkout.
 */
export function UpgradePrompt() {
  const ask = upgradePrompt((st) => st.ask);
  const close = upgradePrompt((st) => st.close);
  const navigate = useNavigate();
  const open = ask !== null;
  const plans = useQuery({ ...plansQuery, staleTime: 300_000, enabled: open }).data;
  const sub = useQuery({ queryKey: qk.subscription, queryFn: () => call(api.GET('/api/v1/subscription')), enabled: open }).data;
  const cryptoOnly = useMe().data?.entitlements.asset_scope === 'crypto';
  if (!ask) return null;

  const plan = plans?.find((p) => p.code === ask.plan);
  const name = plan?.name ?? PLAN_NAME[ask.plan];
  const referral = Boolean(sub?.referral_discount_available);
  const trialDays = !referral && sub?.trial_eligible ? plan?.store_products?.find((p) => p.billing_interval === 'month')?.trial_days : null;
  const points = plan ? keyPoints(plan, cryptoOnly) : [];
  // A crypto-only country holds no stocks on any plan, so Elite's pitch must not offer them.
  const pitch = cryptoOnly && ask.plan === 'elite' ? 'Every feature and the most credits' : PITCH[ask.plan];
  const go = () => {
    close();
    void navigate({ to: '/plans' });
  };

  return (
    <Modal
      open
      onClose={close}
      title={`${ask.subject} is part of ${name}`}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={close}>
            Not now
          </Button>
          <Button variant="primary" icon="workspace_premium" onClick={go}>
            {trialDays ? `Start your ${trialDays}-day free trial` : `Upgrade to ${name}`}
          </Button>
        </>
      }
    >
      <div className={s.body}>
        <span className={s.icon}>
          <Icon name="lock" size={18} />
        </span>
        {pitch && <p className={s.pitch}>{`${name}: ${pitch.charAt(0).toLowerCase()}${pitch.slice(1)}.`}</p>}
        {points.length > 0 && (
          <ul className={s.points}>
            {points.map((p) => (
              <li key={p}>
                <Icon name="check" size={16} className={s.check} />
                {p}
              </li>
            ))}
          </ul>
        )}
        {trialDays ? (
          <p className={s.offer}>Try {name} free for {trialDays} days. Cancel before the trial ends and you won't be charged.</p>
        ) : referral ? (
          <p className={s.offer}>Your referral gives you the first month at half price.</p>
        ) : null}
      </div>
    </Modal>
  );
}
